import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Zap, 
  BarChart3, 
  Bot, 
  Workflow, 
  CheckCircle, 
  ArrowRight,
  Smartphone,
  Database,
  Shield,
  Globe,
  Target,
  Users,
  Clock,
  Code
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PowerPlatformDevelopment: React.FC = () => {
  const navigate = useNavigate();
  const powerPlatformTools = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Power Apps",
      description: "Build custom business applications without writing code. Create apps that work on web and mobile devices.",
      features: ["Drag-and-drop interface", "No-code development", "Mobile responsive", "Integration ready"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Power BI",
      description: "Transform your data into stunning visualizations and interactive reports for better decision-making.",
      features: ["Real-time dashboards", "Advanced analytics", "Data modeling", "Collaborative reports"]
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: "Power Automate",
      description: "Automate repetitive tasks and workflows across your organization to improve efficiency.",
      features: ["Workflow automation", "Approval processes", "Scheduled tasks", "Multi-platform integration"]
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Power Virtual Agents",
      description: "Create intelligent chatbots that can handle customer inquiries and support requests 24/7.",
      features: ["AI-powered responses", "Multi-language support", "Integration with Teams", "Analytics dashboard"]
    }
  ];

  const benefits = [
    "Reduce development time by 70%",
    "Lower costs compared to custom development",
    "No coding skills required for basic apps",
    "Seamless integration with Microsoft 365",
    "Scalable solutions that grow with your business",
    "Built-in security and compliance features"
  ];

  const developmentProcess = [
    {
      step: "01",
      title: "Requirements Analysis",
      description: "We analyze your business needs and identify the best Power Platform tools for your solution."
    },
    {
      step: "02",
      title: "Solution Design",
      description: "Design the architecture and user experience for your Power Platform solution."
    },
    {
      step: "03",
      title: "Development & Configuration",
      description: "Build and configure your Power Platform solution using best practices and templates."
    },
    {
      step: "04",
      title: "Testing & Optimization",
      description: "Thorough testing to ensure performance, security, and user experience meet requirements."
    },
    {
      step: "05",
      title: "Deployment & Training",
      description: "Deploy your solution and provide comprehensive training to your team."
    },
    {
      step: "06",
      title: "Support & Maintenance",
      description: "Ongoing support, updates, and optimization to keep your solution running smoothly."
    }
  ];

  const useCases = [
    {
      title: "Customer Service Portal",
      description: "Build a self-service portal for customers to submit tickets and track their status.",
      icon: <Users className="w-8 h-8" />
    },
    {
      title: "Sales Dashboard",
      description: "Create interactive dashboards to track sales performance and pipeline metrics.",
      icon: <BarChart3 className="w-8 h-8" />
    },
    {
      title: "HR Onboarding App",
      description: "Streamline employee onboarding with automated workflows and digital forms.",
      icon: <Workflow className="w-8 h-8" />
    },
    {
      title: "Inventory Management",
      description: "Track inventory levels, automate reorder processes, and generate reports.",
      icon: <Database className="w-8 h-8" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <SEOHead 
        title="Power Platform Development - Custom Apps & Automation Solutions"
        description="Professional Power Platform development services including Power Apps, Power Automate, Power BI, and Power Virtual Agents. Custom business applications and workflow automation."
        keywords="Power Platform development, Power Apps development, Power Automate, Power BI, Power Virtual Agents, business automation"
        canonical="/power-platform-development"
      />
             {/* Hero Section */}
       <section className="pt-48 pb-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Zap className="w-12 h-12 mr-4" />
              <h1 className="text-5xl font-bold">Power Platform Development</h1>
            </div>
            <p className="text-xl text-purple-100 mb-8">
              Harness the power of Microsoft's low-code platform to build custom business solutions 
              that drive efficiency and innovation across your organization.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-50 transition-all duration-300"
              onClick={() => navigate('/contact')}
            >
              Start Building Today
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Power Platform Tools */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Power Platform Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Microsoft Power Platform provides four powerful tools that work together to create 
              comprehensive business solutions without the need for extensive coding.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {powerPlatformTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-purple-600 mb-4">{tool.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  {tool.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {tool.description}
                </p>
                <div className="space-y-2">
                  {tool.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Power Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the benefits of rapid development, cost savings, and powerful integration capabilities.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Popular Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how organizations are using Power Platform to solve real business challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-purple-600 mb-4 flex justify-center">{useCase.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {useCase.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Development Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a proven methodology to deliver high-quality Power Platform solutions on time and within budget.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {developmentProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Build Your Solution?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Let our Power Platform experts help you create custom solutions that transform your business. 
              Start with a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-50 transition-all duration-300"
                onClick={() => navigate('/contact')}
              >
                Schedule Consultation
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-purple-600 transition-all duration-300"
              >
                View Portfolio
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PowerPlatformDevelopment; 